import React, { useEffect, useState, useRef } from 'react'
import { getSessionHeaders } from '../../lib/serverSession';
import { getServerSessionId } from '../../lib/serverSession'
import { filterAndRank } from '../../lib/searchRelevance'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']
const AUTO_REFRESH_INTERVAL_MS = 20000

// Allowed MIME types and their corresponding safe extensions
const ALLOWED_EXTENSIONS: { [key: string]: string } = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'application/pdf': 'pdf'
}

// Magic bytes for file type validation
const FILE_SIGNATURES: { [key: string]: number[] } = {
  'image/jpeg': [0xFF, 0xD8, 0xFF],
  'image/png': [0x89, 0x50, 0x4E, 0x47],
  'image/gif': [0x47, 0x49, 0x46],
  'image/webp': [0x52, 0x49, 0x46, 0x46], // RIFF header
  'application/pdf': [0x25, 0x50, 0x44, 0x46] // %PDF
}

// Validate file by checking magic bytes
const validateFileMagicBytes = async (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = (e) => {
      if (!e.target?.result) {
        resolve(false)
        return
      }
      const arr = new Uint8Array(e.target.result as ArrayBuffer)
      const signature = FILE_SIGNATURES[file.type]
      if (!signature) {
        resolve(false)
        return
      }
      const matches = signature.every((byte, index) => arr[index] === byte)
      resolve(matches)
    }
    reader.onerror = () => resolve(false)
    reader.readAsArrayBuffer(file.slice(0, 8))
  })
}

// Sanitize filename
const sanitizeFileName = (name: string): string => {
  return name
    .replace(/[\/\\]/g, '_')
    .replace(/\0/g, '')
    .replace(/\.\./g, '_')
    .substring(0, 100)
}

interface TicketAttachment {
  url: string
  signedUrl?: string
  publicUrl?: string
  bucket?: string
  path?: string
  name: string
  size: number
  type: string
}

interface SupportTicket {
  id: number
  ticket_number: string
  user_id: number | null
  title: string
  description: string
  category: string
  priority: string
  status: string
  assigned_agent_id: number | null
  guest_name: string | null
  guest_email: string | null
  created_at: string
  updated_at: string
  first_response_at: string | null
  resolved_at: string | null
  closed_at: string | null
  user?: {
    id: number
    username: string
    email: string
    full_name: string | null
  }
  agent?: {
    id: number
    agent_name: string
    agent_email: string
  }
}

interface TicketResponse {
  id: number
  ticket_id: number
  responder_type: 'agent' | 'user' | 'system'
  responder_id: number | null
  message: string
  is_internal: boolean
  attachments: TicketAttachment[]
  created_at: string
  responder?: {
    id: number
    username?: string
    agent_name?: string
    email: string
  }
}

interface SupportAgent {
  id: number
  agent_name: string
  agent_email: string
  is_active: boolean
}

const SupportInbox: React.FC<{ adminUser?: any }> = ({ adminUser }) => {
  const [loading, setLoading] = useState(true)
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null)
  const [responses, setResponses] = useState<TicketResponse[]>([])
  const [replyAttachments, setReplyAttachments] = useState<File[]>([])
  const [uploadingAttachments, setUploadingAttachments] = useState(false)
  const replyFileInputRef = useRef<HTMLInputElement>(null)
  const [reply, setReply] = useState('')
  const [isInternalNote, setIsInternalNote] = useState(false)
  const [agents, setAgents] = useState<SupportAgent[]>([])
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterPriority, setFilterPriority] = useState<string>('all')
  const [filterAssigned, setFilterAssigned] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [adminAgentId, setAdminAgentId] = useState<number | null>(null)
  const [cannedResponses, setCannedResponses] = useState<Array<{ id: number; title: string; content: string; category: string; shortcut_key: string | null }>>([])
  const [showCannedPicker, setShowCannedPicker] = useState(false)
  const [cannedSearch, setCannedSearch] = useState('')
  const [bulkSelected, setBulkSelected] = useState<Set<number>>(new Set())
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkProcessing, setBulkProcessing] = useState(false)
  const [auditLog, setAuditLog] = useState<Array<{ id: number; action: string; field_changed: string | null; old_value: string | null; new_value: string | null; performed_by: string; created_at: string }>>([])
  const [showAuditLog, setShowAuditLog] = useState(false)
  const [auditLogPage, setAuditLogPage] = useState(1)
  const AUDIT_PAGE_SIZE = 20
  // v2.9.55: Translation + CSV export state
  const [translatedMessages, setTranslatedMessages] = useState<Record<number, string>>({})
  const [translatingId, setTranslatingId] = useState<number | null>(null)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [newTicketIds, setNewTicketIds] = useState<number[]>([])
  const [lastRefreshAt, setLastRefreshAt] = useState<string | null>(null)
  const knownTicketIdsRef = useRef<Set<number>>(new Set())
  const initialTicketLoadCompleteRef = useRef(false)

  useEffect(() => {
    initializeInbox()
    loadCannedResponses()
  }, [adminUser?.email])

  useEffect(() => {
    if (!adminUser?.email) return

    const intervalId = window.setInterval(async () => {
      await loadTickets()

      if (selectedTicket?.id) {
        await loadTicketResponses(selectedTicket.id)
      }
    }, AUTO_REFRESH_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [adminUser?.email, selectedTicket?.id])

  useEffect(() => {
    if (selectedTicket) {
      loadTicketResponses(selectedTicket.id)
      loadAuditLog(selectedTicket.id)
      setShowAuditLog(false)
      setAuditLogPage(1)
    }
  }, [selectedTicket])

  const loadAuditLog = async (ticketId: number) => {
    try {
      const res = await fetch('/api/admin/support', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_audit_log', data: { ticketId } })
      })
      const json = await res.json()
      if (json.success) setAuditLog(json.data || [])
    } catch (err) { console.error('Failed to load audit log:', err) }
  }

  const initializeInbox = async () => {
    setLoading(true)
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'initialize_inbox', data: { adminEmail: adminUser?.email } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      const initData = result.data || result
      setAdminAgentId(initData.adminAgentId || null)
      setAgents(initData.agents || [])

      // Load tickets
      await loadTickets({ suppressNotifications: true })
    } catch (error) {
      console.error('Error initializing inbox:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadCannedResponses = async () => {
    try {
      const res = await fetch('/api/admin/support', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_canned_responses', data: {} })
      })
      const json = await res.json()
      if (json.success) setCannedResponses(json.data || [])
    } catch (err) { console.error('Failed to load canned responses:', err) }
  }

  const insertCannedResponse = async (cr: { id: number; content: string }) => {
    setReply(prev => prev ? prev + '\n\n' + cr.content : cr.content)
    setShowCannedPicker(false)
    setCannedSearch('')
    // Increment usage count in background
    try {
      await fetch('/api/admin/support', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'increment_canned_response_usage', data: { id: cr.id } })
      })
    } catch (_) {}
  }

  const showBrowserNotification = (ticket: SupportTicket) => {
    if (typeof window === 'undefined' || !("Notification" in window)) return
    if (Notification.permission !== 'granted') return

    const requesterName = ticket.user?.full_name || ticket.user?.username || ticket.guest_name || 'Guest user'

    const notification = new Notification('New support ticket received', {
      body: `${ticket.ticket_number} • ${requesterName}`,
      tag: `support-ticket-${ticket.id}`,
      renotify: false
    } as any)

    notification.onclick = () => {
      window.focus()
      notification.close()
    }
  }

  const loadTickets = async ({ suppressNotifications = false }: { suppressNotifications?: boolean } = {}) => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_tickets' })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      const ticketData = result.data || result
      const nextTickets = ticketData.tickets || []
      const knownTicketIds = knownTicketIdsRef.current
      const incomingTickets =
        initialTicketLoadCompleteRef.current && !suppressNotifications
          ? nextTickets.filter((ticket: SupportTicket) => !knownTicketIds.has(ticket.id))
          : []

      setTickets(nextTickets)
      setSelectedTicket((currentSelected) => {
        if (!currentSelected) return currentSelected
        return nextTickets.find((ticket: SupportTicket) => ticket.id === currentSelected.id) || null
      })

      if (incomingTickets.length > 0) {
        setNewTicketIds((currentIds) => Array.from(new Set([...incomingTickets.map((ticket: SupportTicket) => ticket.id), ...currentIds])))
        incomingTickets.forEach(showBrowserNotification)
      }

      knownTicketIdsRef.current = new Set(nextTickets.map((ticket: SupportTicket) => ticket.id))
      initialTicketLoadCompleteRef.current = true
      setLastRefreshAt(new Date().toISOString())
    } catch (error) {
      console.error('Error loading tickets:', error)
    }
  }

  const loadTicketResponses = async (ticketId: number) => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_ticket_responses', data: { ticketId } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      setResponses(result.data || [])
    } catch (error) {
      console.error('Error loading responses:', error)
    }
  }

  // Handle file selection for reply with security validation
  const handleReplyFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles = files.filter(file => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        alert(`Invalid file type: ${file.name}. Only JPEG, PNG, GIF, WebP images, and PDF documents are allowed.`)
        return false
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`File too large: ${file.name}. Maximum size is 5MB.`)
        return false
      }
      return true
    })
    setReplyAttachments(prev => [...prev, ...validFiles])
    if (replyFileInputRef.current) replyFileInputRef.current.value = ''
  }

  // Remove attachment from reply
  const removeReplyAttachment = (index: number) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== index))
  }

  // Upload attachment to storage with security validations
  const uploadAttachment = async (file: File, ticketId: number): Promise<TicketAttachment | null> => {
    try {
      // SECURITY: Validate file type against whitelist
      if (!ALLOWED_EXTENSIONS[file.type]) {
        console.error('🚫 Security: Invalid file type rejected:', file.type)
        return null
      }

      // SECURITY: Validate file size
      if (file.size > MAX_FILE_SIZE) {
        console.error('🚫 Security: File too large:', file.size)
        return null
      }

      // SECURITY: Validate magic bytes to prevent MIME type spoofing
      const validMagicBytes = await validateFileMagicBytes(file)
      if (!validMagicBytes) {
        console.error('🚫 Security: File magic bytes do not match declared MIME type. Possible file type spoofing detected.')
        alert('Security warning: File content does not match declared file type. Upload rejected.')
        return null
      }

      // SECURITY: Use whitelisted extension instead of user-provided extension
      const safeExtension = ALLOWED_EXTENSIONS[file.type]
      const sanitizedName = sanitizeFileName(file.name)

      // Generate unique filename with safe extension
      const timestamp = Date.now()
      const randomStr = Math.random().toString(36).substring(2, 8)
      const fileName = `ticket-${ticketId}/admin/${timestamp}-${randomStr}.${safeExtension}`

      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'upload_attachment',
          data: { ticketId, fileName, fileType: file.type, sanitizedName, fileSize: file.size }
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      // Step 2: Actually upload the file bytes to the signed URL
      const uploadData = result.data || result
      const { signedUrl, path: storagePath, bucket = 'ticket-attachments' } = uploadData
      if (!signedUrl) throw new Error('No signed upload URL returned')

      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      })
      if (!uploadResponse.ok) throw new Error(`File upload failed: ${uploadResponse.status}`)

      // Step 3: Construct the public URL from the storage path
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
      const publicUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${storagePath}`

      console.log('✅ Secure admin file upload successful:', sanitizedName)

      return {
        url: publicUrl,
        publicUrl,
        bucket,
        path: storagePath,
        name: sanitizedName,
        size: file.size,
        type: file.type
      }
    } catch (error) {
      console.error('Error uploading attachment:', error)
      return null
    }
  }

  const sendReply = async () => {
    if (!reply.trim() && replyAttachments.length === 0) {
      alert('Please enter a message or attach an image before sending.')
      return
    }

    if (!selectedTicket) {
      alert('No ticket selected.')
      return
    }

    if (!adminAgentId) {
      console.error('❌ Admin agent ID not found. Admin email:', adminUser?.email)
      alert(`ERROR: You are not registered as a support agent.\n\nYour email (${adminUser?.email}) is not in the support_agents table.\n\nPlease contact the system administrator to add you as a support agent.`)
      return
    }

    try {
      setUploadingAttachments(true)

      console.log('📤 Sending reply:', {
        ticket_id: selectedTicket.id,
        responder_id: adminAgentId,
        is_internal_note: isInternalNote,
        attachments_count: replyAttachments.length
      })

      // Upload attachments first
      const uploadedAttachments: TicketAttachment[] = []
      for (const file of replyAttachments) {
        const attachment = await uploadAttachment(file, selectedTicket.id)
        if (attachment) {
          uploadedAttachments.push(attachment)
        }
      }

      // Create response with attachments via API
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'send_reply',
          data: {
            ticketId: selectedTicket.id,
            responderId: adminAgentId,
            message: reply.trim() || (uploadedAttachments.length > 0 ? 'Attached images:' : ''),
            isInternalNote,
            attachments: uploadedAttachments,
            isFirstResponse: !selectedTicket.first_response_at
          }
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      console.log('✅ Reply sent successfully')

      // Reload responses and clear input
      setReply('')
      setIsInternalNote(false)
      setReplyAttachments([])
      await loadTicketResponses(selectedTicket.id)
      await loadAuditLog(selectedTicket.id)
      await loadTickets()
    } catch (error) {
      console.error('❌ Error sending reply:', error)
      alert('Failed to send reply. Please try again.')
    } finally {
      setUploadingAttachments(false)
    }
  }

  const updateTicketStatus = async (ticketId: number, newStatus: string) => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'update_ticket_status',
          data: {
            ticketId,
            newStatus
          }
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      await loadTickets()
      if (selectedTicket?.id === ticketId) {
        await loadAuditLog(ticketId)
      }
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Failed to update status. Please try again.')
    }
  }

  const updateTicketPriority = async (ticketId: number, newPriority: string) => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'update_ticket_priority',
          data: { ticketId, newPriority }
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      await loadTickets()
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, priority: newPriority })
        await loadAuditLog(ticketId)
      }
    } catch (error) {
      console.error('Error updating priority:', error)
      alert('Failed to update priority. Please try again.')
    }
  }

  const assignTicket = async (ticketId: number, agentId: number | null) => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'assign_ticket',
          data: { ticketId, agentId }
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      await loadTickets()
      if (selectedTicket?.id === ticketId) {
        const agent = agents.find(a => a.id === agentId)
        setSelectedTicket({
          ...selectedTicket,
          assigned_agent_id: agentId,
          agent: agent ? { id: agent.id, agent_name: agent.agent_name, agent_email: agent.agent_email } : undefined
        })
        await loadAuditLog(ticketId)
      }
    } catch (error) {
      console.error('Error assigning ticket:', error)
      alert('Failed to assign ticket. Please try again.')
    }
  }

  const openTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket)
    setNewTicketIds((currentIds) => currentIds.filter(id => id !== ticket.id))
  }

  const toggleBulkSelect = (id: number) => {
    setBulkSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const selectAllFiltered = () => {
    if (bulkSelected.size === filteredTickets.length) {
      setBulkSelected(new Set())
    } else {
      setBulkSelected(new Set(filteredTickets.map(t => t.id)))
    }
  }

  const executeBulkAction = async (action: string, value?: string | number) => {
    if (bulkSelected.size === 0) return
    setBulkProcessing(true)
    try {
      const payload: any = { ticketIds: Array.from(bulkSelected) }
      if (action === 'status') payload.newStatus = value
      if (action === 'priority') payload.newPriority = value
      if (action === 'assign') payload.assignedAgentId = value
      const res = await fetch('/api/admin/support', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'bulk_update_tickets', data: payload })
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error)
      setBulkSelected(new Set())
      setBulkMode(false)
      await loadTickets()
    } catch (err) {
      console.error('Bulk action failed:', err)
      alert('Bulk action failed. Please try again.')
    }
    setBulkProcessing(false)
  }

  // Filter tickets based on status, priority, assigned agent, and search.
  // The status/priority/assigned filters are applied first as hard predicates,
  // then filterAndRank is applied to the search term so that an exact match on
  // the requester's identity (username/email/guest_name/guest_email) ranks
  // above a substring match in ticket meta (title/description/number).
  const ticketsAfterHardFilters = tickets.filter(ticket => {
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority

    // Handle assigned filter: 'all', 'unassigned', or specific agent ID
    let matchesAssigned = true
    if (filterAssigned === 'unassigned') {
      matchesAssigned = ticket.assigned_agent_id === null
    } else if (filterAssigned !== 'all') {
      matchesAssigned = ticket.assigned_agent_id === parseInt(filterAssigned)
    }

    return matchesStatus && matchesPriority && matchesAssigned
  })

  // Apply search via filterAndRank: requester identity (user.username,
  // user.email, guest_name, guest_email) is the primary tier so an exact
  // match on the ticket owner ranks above a substring match in title /
  // description / ticket_number. Inclusion semantics are preserved — every
  // ticket that previously matched the OR-of-includes still matches here.
  const filteredTickets = filterAndRank(
    ticketsAfterHardFilters,
    searchQuery,
    (ticket) => ({
      primary: [
        ticket.user?.username,
        ticket.user?.email,
        ticket.guest_name,
        ticket.guest_email,
        ticket.ticket_number,
      ],
      peripheral: [ticket.title, ticket.description],
    }),
  )

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-900/60 text-blue-100 border border-blue-700/70'
      case 'in_progress': return 'bg-amber-900/60 text-amber-100 border border-amber-700/70'
      case 'waiting_user': return 'bg-orange-900/60 text-orange-100 border border-orange-700/70'
      case 'resolved': return 'bg-green-900/60 text-green-100 border border-green-700/70'
      case 'closed': return 'bg-slate-700/80 text-slate-100 border border-slate-500/70'
      default: return 'bg-slate-700/80 text-slate-100 border border-slate-500/70'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-900/70 text-red-100 border border-red-700/70'
      case 'high': return 'bg-orange-900/60 text-orange-100 border border-orange-700/70'
      case 'medium': return 'bg-amber-900/60 text-amber-100 border border-amber-700/70'
      case 'low': return 'bg-sky-900/60 text-sky-100 border border-sky-700/70'
      default: return 'bg-slate-700/80 text-slate-100 border border-slate-500/70'
    }
  }

  const formatTicketTimestamp = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const clearNewTicketAlerts = () => {
    setNewTicketIds([])
  }

  // v2.9.55: Translate a user message to English
  const handleTranslateMessage = async (responseId: number, message: string) => {
    if (translatedMessages[responseId]) return // already translated
    setTranslatingId(responseId)
    try {
      const res = await fetch('/api/admin/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'translate_message', data: { text: message, targetLang: 'en' } })
      })
      const json = await res.json()
      if (json.success && json.data?.translatedText) {
        setTranslatedMessages(prev => ({ ...prev, [responseId]: json.data.translatedText }))
      } else {
        alert(json.error || 'Translation failed. Please try again.')
      }
    } catch (err) {
      console.error('Translation error:', err)
      alert('Translation failed. Please check GOOGLE_TRANSLATE_API_KEY is configured.')
    } finally {
      setTranslatingId(null)
    }
  }

  // v2.9.55: Export tickets as CSV
  const handleExportCsv = async () => {
    setExportingCsv(true)
    try {
      const res = await fetch('/api/admin/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'export_tickets_csv', data: { statusFilter: filterStatus !== 'all' ? filterStatus : undefined } })
      })
      const json = await res.json()
      if (!json.success || !json.data?.csv) throw new Error(json.error || 'Export failed')
      // Trigger CSV download
      const blob = new Blob([json.data.csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `support_tickets_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('CSV export error:', err)
      alert('Failed to export CSV. Please try again.')
    } finally {
      setExportingCsv(false)
    }
  }

  // Simple heuristic: detect non-English text (contains non-ASCII chars or known patterns)
  const looksNonEnglish = (text: string): boolean => {
    if (!text || text.length < 10) return false
    const nonAsciiCount = (text.match(/[^\x00-\x7F]/g) || []).length
    return nonAsciiCount > text.length * 0.15
  }

  return (
    <div className="space-y-4">
      {/* Header with Filters */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-semibold text-white">Support Inbox</h3>
            <div className="flex items-center gap-2 rounded-full bg-gray-700 px-3 py-1 text-sm text-gray-200">
              <span className="relative text-lg leading-none">
                🔔
                {newTicketIds.length > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                    {newTicketIds.length > 99 ? '99+' : newTicketIds.length}
                  </span>
                )}
              </span>
              <span>{newTicketIds.length > 0 ? `${newTicketIds.length} new ticket${newTicketIds.length === 1 ? '' : 's'}` : 'No new tickets'}</span>
            </div>
            <button
              onClick={clearNewTicketAlerts}
              disabled={newTicketIds.length === 0}
              className="px-3 py-1 rounded text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Clear alerts
            </button>
            <button onClick={() => { setBulkMode(!bulkMode); setBulkSelected(new Set()) }}
              className={`px-3 py-1 rounded text-sm font-medium ${bulkMode ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
              {bulkMode ? '✕ Exit Bulk' : '☑ Bulk Actions'}
            </button>
            <button onClick={handleExportCsv} disabled={exportingCsv}
              className="px-3 py-1 rounded text-sm font-medium bg-green-700 text-white hover:bg-green-600 disabled:opacity-50"
              title="Export tickets to CSV">
              {exportingCsv ? '⏳ Exporting...' : '📥 Export CSV'}
            </button>
          </div>
          <div className="flex flex-col items-start gap-3 md:items-end">
            <div className="text-xs text-gray-300">
              Auto-refresh every 20s{lastRefreshAt ? ` • Last checked ${new Date(lastRefreshAt).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : ''}
            </div>
            <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md text-sm"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md text-sm"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="waiting_user">Waiting User</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md text-sm"
            >
              <option value="all">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              value={filterAssigned}
              onChange={(e) => setFilterAssigned(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md text-sm"
            >
              <option value="all">All Assigned</option>
              <option value="unassigned">Unassigned</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.agent_name}
                </option>
              ))}
            </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Action Toolbar */}
      {bulkMode && (
        <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-3 flex flex-wrap items-center gap-3">
          <button onClick={selectAllFiltered} className="px-3 py-1.5 bg-gray-700 text-white rounded text-sm hover:bg-gray-600">
            {bulkSelected.size === filteredTickets.length ? 'Deselect All' : `Select All (${filteredTickets.length})`}
          </button>
          <span className="text-sm text-yellow-300 font-medium">{bulkSelected.size} selected</span>
          {bulkSelected.size > 0 && (
            <>
              <select onChange={e => { if (e.target.value) executeBulkAction('status', e.target.value); e.target.value = '' }}
                className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" disabled={bulkProcessing}>
                <option value="">Set Status...</option>
                <option value="open">Open</option><option value="in_progress">In Progress</option>
                <option value="waiting_user">Waiting User</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
              </select>
              <select onChange={e => { if (e.target.value) executeBulkAction('priority', e.target.value); e.target.value = '' }}
                className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" disabled={bulkProcessing}>
                <option value="">Set Priority...</option>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
              <select onChange={e => { executeBulkAction('assign', e.target.value ? parseInt(e.target.value) : 0); e.target.value = '' }}
                className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" disabled={bulkProcessing}>
                <option value="">Assign To...</option>
                <option value="0">Unassigned</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.agent_name}</option>)}
              </select>
              {bulkProcessing && <span className="text-sm text-yellow-400 animate-pulse">Processing...</span>}
            </>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Ticket List */}
        <div className="lg:col-span-1 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-700">
            <h4 className="font-medium text-white">Tickets ({filteredTickets.length})</h4>
          </div>
          <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
            {loading ? (
              <div className="p-4 text-center text-white">Loading tickets...</div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-4 text-center text-white">No tickets found</div>
            ) : (
              <ul className="divide-y divide-gray-700">
                {filteredTickets.map(ticket => (
                  <li
                    key={ticket.id}
                    className={`p-4 cursor-pointer hover:bg-gray-700 transition-colors ${
                      selectedTicket?.id === ticket.id ? 'bg-blue-900 border-l-4 border-blue-500' : ''
                    }`}
                    onClick={() => bulkMode ? toggleBulkSelect(ticket.id) : openTicket(ticket)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      {bulkMode && (
                        <input type="checkbox" checked={bulkSelected.has(ticket.id)}
                          onChange={() => toggleBulkSelect(ticket.id)}
                          className="mt-1 mr-3 h-4 w-4 rounded border-gray-500 text-yellow-600 focus:ring-yellow-500 cursor-pointer" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white mb-1">
                          {ticket.ticket_number}
                        </div>
                        <div className="font-medium text-white truncate">
                          {ticket.title}
                        </div>
                        <div className="mt-1 text-xs text-gray-300">
                          Created {formatTicketTimestamp(ticket.created_at)}
                        </div>
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center text-sm font-medium text-white">
                        {ticket.user?.username?.[0]?.toUpperCase() || ticket.guest_name?.[0]?.toUpperCase() || 'G'}
                      </div>
                      <div className="flex-1 min-w-0">
                        {ticket.user?.id ? (
                          <a
                            href={`#user-management?userId=${ticket.user.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-medium text-blue-400 hover:text-blue-300 truncate block hover:underline"
                            title="Open user in User Management"
                          >
                            {ticket.user?.full_name || ticket.user?.username}
                          </a>
                        ) : (
                          <div className="text-sm font-medium text-white truncate">
                            {ticket.guest_name || 'Guest'}
                          </div>
                        )}
                        <div className="text-sm text-white truncate">
                          {ticket.user?.email || ticket.guest_email}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {newTicketIds.includes(ticket.id) && (
                        <span className="px-2 py-1 text-sm font-medium rounded-full bg-red-900/70 text-red-200 border border-red-700">
                          New
                        </span>
                      )}
                      <span className={`px-2 py-1 text-sm font-medium rounded-full ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-1 text-sm font-medium rounded-full ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <span className="text-sm text-white">
                        {formatTicketTimestamp(ticket.created_at)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Ticket Detail Panel */}
        <div className="lg:col-span-2 bg-gray-800 border border-gray-700 rounded-lg">
          {selectedTicket ? (
            <div className="flex flex-col h-[calc(100vh-180px)] min-h-[600px]">
              {/* Ticket Header */}
              <div className="px-6 py-4 border-b border-gray-700">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="text-sm text-white mb-1">{selectedTicket.ticket_number}</div>
                    <h3 className="text-lg font-semibold text-white">{selectedTicket.title}</h3>
                  </div>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="text-gray-200 hover:text-white"
                  >
                    ✕
                  </button>
                </div>

                {/* User Info Card */}
                <div className="bg-gray-700 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-lg font-medium text-white">
                      {selectedTicket.user?.username?.[0]?.toUpperCase() || selectedTicket.guest_name?.[0]?.toUpperCase() || 'G'}
                    </div>
                    <div className="flex-1">
                      {selectedTicket.user?.id ? (
                        <a
                          href={`#user-management?userId=${selectedTicket.user.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-400 hover:text-blue-300 hover:underline"
                          title="Open user in User Management"
                        >
                          {selectedTicket.user?.full_name || selectedTicket.user?.username}
                        </a>
                      ) : (
                        <div className="font-medium text-white">
                          {selectedTicket.guest_name || 'Guest User'}
                        </div>
                      )}
                      <div className="text-sm text-gray-300">
                        {selectedTicket.user?.email || selectedTicket.guest_email}
                      </div>
                      {selectedTicket.user?.username && (
                        <div className="text-sm text-white">@{selectedTicket.user.username}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ticket Controls */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => updateTicketStatus(selectedTicket.id, e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md text-sm"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="waiting_user">Waiting User</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Priority</label>
                    <select
                      value={selectedTicket.priority}
                      onChange={(e) => updateTicketPriority(selectedTicket.id, e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md text-sm"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  {/* Assign Agent */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Assigned To</label>
                    <select
                      value={selectedTicket.assigned_agent_id || ''}
                      onChange={(e) => assignTicket(selectedTicket.id, e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md text-sm"
                    >
                      <option value="">Unassigned</option>
                      {agents.map(agent => (
                        <option key={agent.id} value={agent.id}>
                          {agent.agent_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Metadata */}
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-white">
                  <div>
                    <span className="font-medium">Category:</span> {selectedTicket.category}
                  </div>
                  <div>
                    <span className="font-medium">Created:</span> {new Date(selectedTicket.created_at).toLocaleString()}
                  </div>
                  {selectedTicket.first_response_at && (
                    <div>
                      <span className="font-medium">First Response:</span> {new Date(selectedTicket.first_response_at).toLocaleString()}
                    </div>
                  )}
                  {selectedTicket.resolved_at && (
                    <div>
                      <span className="font-medium">Resolved:</span> {new Date(selectedTicket.resolved_at).toLocaleString()}
                    </div>
                  )}
                  {selectedTicket.closed_at && (
                    <div>
                      <span className="font-medium">Closed:</span> {new Date(selectedTicket.closed_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              {/* Conversation Thread */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-900 min-h-[200px]">
                {/* Original Ticket */}
                <div className="bg-gray-700 rounded-lg p-4 border-l-4 border-blue-500">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-sm font-medium text-white">
                      {selectedTicket.user?.username?.[0]?.toUpperCase() || selectedTicket.guest_name?.[0]?.toUpperCase() || 'G'}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-white">
                        {selectedTicket.user?.full_name || selectedTicket.user?.username || selectedTicket.guest_name || 'Guest'}
                      </div>
                      <div className="text-sm text-white">
                        {new Date(selectedTicket.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-white whitespace-pre-wrap">
                    {selectedTicket.description}
                  </div>
                </div>

                {/* Responses */}
                {responses.map(response => (
                  <div
                    key={response.id}
                    className={`rounded-lg p-4 ${
                      response.is_internal
                        ? 'bg-yellow-900 border-l-4 border-yellow-500'
                        : response.responder_type === 'agent'
                        ? 'bg-blue-900 border-l-4 border-blue-500'
                        : 'bg-gray-700 border-l-4 border-gray-500'
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        response.is_internal
                          ? 'bg-yellow-600 text-white'
                          : response.responder_type === 'agent'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-600 text-white'
                      }`}>
                        {response.responder?.agent_name?.[0]?.toUpperCase() ||
                         response.responder?.username?.[0]?.toUpperCase() ||
                         'S'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-white">
                            {response.responder?.agent_name || response.responder?.username || 'System'}
                          </div>
                          {response.is_internal && (
                            <span className="px-2 py-0.5 text-sm font-medium bg-yellow-600 text-white rounded">
                              Internal Note
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-white">
                          {new Date(response.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-white whitespace-pre-wrap">
                      {response.message}
                    </div>

                    {/* v2.9.55: Translation UI for user messages */}
                    {response.responder_type === 'user' && (looksNonEnglish(response.message) || translatedMessages[response.id]) && (
                      <div className="mt-2">
                        {translatedMessages[response.id] ? (
                          <div className="bg-green-900/40 border border-green-700 rounded p-2 mt-1">
                            <div className="text-xs text-green-400 font-medium mb-1">🌐 Translated to English:</div>
                            <div className="text-sm text-green-100 whitespace-pre-wrap">{translatedMessages[response.id]}</div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleTranslateMessage(response.id, response.message)}
                            disabled={translatingId === response.id}
                            className="px-2 py-1 text-xs font-medium bg-indigo-700 text-white rounded hover:bg-indigo-600 disabled:opacity-50"
                          >
                            {translatingId === response.id ? '⏳ Translating...' : '🌐 Translate to English'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Display attachments */}
                    {/* v2.9.57: SECURITY — validate attachment URL protocol (matches TicketingSystem.tsx) */}
                    {response.attachments && response.attachments.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {response.attachments.filter(att => (att.signedUrl || att.url)?.startsWith('https://')).map((attachment, idx) => {
                          const attachmentUrl = attachment.signedUrl || attachment.url
                          return (
                          <a
                            key={idx}
                            href={attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={attachmentUrl}
                              alt={attachment.name}
                              className="max-w-[200px] max-h-[150px] rounded-lg border border-gray-600 hover:border-blue-500 transition-colors"
                            />
                          </a>
                        )})}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Audit Log Toggle */}
              <div className="border-t border-gray-700">
                <button onClick={() => setShowAuditLog(!showAuditLog)}
                  className="w-full px-6 py-2 text-sm text-gray-200 hover:text-white hover:bg-gray-700/50 flex items-center justify-between transition-colors">
                  <span>📋 Audit Log ({auditLog.length} entries)</span>
                  <span>{showAuditLog ? '▲' : '▼'}</span>
                </button>
                {showAuditLog && auditLog.length > 0 && (
                  <div className="max-h-48 overflow-y-auto px-6 pb-3 space-y-1.5">
                    {auditLog.slice(0, auditLogPage * AUDIT_PAGE_SIZE).map(entry => (
                      <div key={entry.id} className="flex items-start gap-2 text-sm">
                        <span className="text-gray-300 whitespace-nowrap mt-0.5">
                          {new Date(entry.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          entry.action.includes('status') ? 'bg-blue-900/50 text-blue-300' :
                          entry.action.includes('priority') ? 'bg-orange-900/50 text-orange-300' :
                          entry.action.includes('assign') ? 'bg-purple-900/50 text-purple-300' :
                          entry.action.includes('reply') ? 'bg-green-900/50 text-green-300' :
                          entry.action.includes('note') ? 'bg-yellow-900/50 text-yellow-300' :
                          'bg-gray-700 text-gray-300'
                        }`}>
                          {entry.action.replace(/_/g, ' ')}
                        </span>
                        {entry.old_value && entry.new_value && (
                          <span className="text-white">
                            <span className="line-through text-red-400/70">{entry.old_value}</span>
                            {' → '}
                            <span className="text-green-400/70">{entry.new_value}</span>
                          </span>
                        )}
                        <span className="text-gray-300 ml-auto">{entry.performed_by}</span>
                      </div>
                    ))}
                    {auditLog.length > auditLogPage * AUDIT_PAGE_SIZE && (
                      <button onClick={() => setAuditLogPage(p => p + 1)}
                        className="w-full text-center text-[10px] text-gray-200 hover:text-white py-1">
                        Show more ({auditLog.length - auditLogPage * AUDIT_PAGE_SIZE} remaining)
                      </button>
                    )}
                  </div>
                )}
                {showAuditLog && auditLog.length === 0 && (
                  <p className="px-6 pb-3 text-sm text-gray-300">No audit entries yet.</p>
                )}
              </div>

              {/* Reply Box */}
              <div className="border-t border-gray-700 p-4 bg-gray-800">
                <div className="space-y-3">
                  {/* Canned Response Picker */}
                  <div className="relative">
                    <button onClick={() => setShowCannedPicker(!showCannedPicker)}
                      className="px-3 py-1.5 bg-gray-700 border border-gray-600 text-gray-300 rounded-md text-sm hover:bg-gray-600 flex items-center gap-1"
                      title="Insert canned response">
                      📝 Canned Responses
                    </button>
                    {showCannedPicker && (
                      <div className="absolute bottom-full left-0 mb-1 w-96 max-h-64 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 overflow-hidden">
                        <div className="p-2 border-b border-gray-700">
                          <input type="text" value={cannedSearch} onChange={e => setCannedSearch(e.target.value)}
                            placeholder="Search or type /shortcut..." autoFocus
                            className="w-full px-2 py-1 bg-gray-700 border border-gray-600 text-white text-sm rounded" />
                        </div>
                        <div className="overflow-y-auto max-h-48">
                          {cannedResponses
                            .filter(cr => {
                              if (!cannedSearch) return true
                              const q = cannedSearch.toLowerCase()
                              return cr.title.toLowerCase().includes(q) || cr.content.toLowerCase().includes(q) ||
                                (cr.shortcut_key && cr.shortcut_key.toLowerCase().includes(q))
                            })
                            .map(cr => (
                              <button key={cr.id} onClick={() => insertCannedResponse(cr)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-700 border-b border-gray-700/50">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-white">{cr.title}</span>
                                  {cr.shortcut_key && <code className="text-[10px] text-white bg-gray-700 px-1 rounded">{cr.shortcut_key}</code>}
                                </div>
                                <p className="text-[11px] text-white truncate mt-0.5">{cr.content.substring(0, 80)}...</p>
                              </button>
                            ))}
                          {cannedResponses.length === 0 && <p className="text-sm text-gray-300 p-3">No canned responses available</p>}
                        </div>
                      </div>
                    )}
                  </div>
                  <textarea
                    value={reply}
                    onChange={(e) => {
                      setReply(e.target.value)
                      // Auto-detect /shortcut commands
                      const val = e.target.value
                      if (val.startsWith('/') && !val.includes(' ')) {
                        const match = cannedResponses.find(cr => cr.shortcut_key === val)
                        if (match) { insertCannedResponse(match) }
                      }
                    }}
                    placeholder="Type your reply or /shortcut..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md text-sm resize-none"
                    rows={3}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        sendReply()
                      }
                    }}
                  />

                  {/* Attachment preview */}
                  {replyAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {replyAttachments.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-600"
                          />
                          <button
                            type="button"
                            onClick={() => removeReplyAttachment(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-sm hover:bg-red-600"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={isInternalNote}
                        onChange={(e) => setIsInternalNote(e.target.checked)}
                        className="rounded border-gray-600 bg-gray-700"
                      />
                      Internal note
                    </label>

                    {/* Image upload button */}
                    <input
                      type="file"
                      ref={replyFileInputRef}
                      onChange={handleReplyFileSelect}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                      multiple
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => replyFileInputRef.current?.click()}
                      className="flex items-center gap-1 text-sm text-gray-200 hover:text-blue-300 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Attach File
                    </button>
                  </div>

                  <button
                    onClick={sendReply}
                    disabled={(!reply.trim() && replyAttachments.length === 0) || uploadingAttachments}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {uploadingAttachments ? 'Uploading...' : 'Send Reply'}
                  </button>
                </div>
                <div className="text-sm text-white mt-1">
                  Press Ctrl+Enter to send
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-white">
              <div className="text-center">
                <div className="text-6xl mb-4">📬</div>
                <p className="text-lg font-medium text-gray-300">Select a ticket to view details</p>
                <p className="text-sm mt-1">Click on a ticket from the list to start</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SupportInbox

